/**
 * Centralized responsive design utilities for consistent styling across all components
 */

import { Theme } from '@mui/material/styles';

// Consistent padding for sections
export const getSectionPadding = (theme: Theme) => ({
  padding: '80px 24px',
  [theme.breakpoints.down('lg')]: {
    padding: '70px 20px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '60px 16px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '50px 12px',
  },
  '@media (max-width: 480px)': {
    padding: '40px 8px',
  },
});

// Consistent container max widths
export const getContainerStyles = (theme: Theme) => ({
  maxWidth: '1280px',
  margin: '0 auto',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    maxWidth: '1100px',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '900px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    padding: '0 16px',
  },
  '@media (max-width: 480px)': {
    padding: '0 12px',
  },
});

// Consistent heading styles
export const getHeadingStyles = (theme: Theme) => ({
  h1: {
    fontSize: '3.5rem',
    lineHeight: 1.2,
    [theme.breakpoints.down('lg')]: {
      fontSize: '3rem',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
    '@media (max-width: 480px)': {
      fontSize: '1.75rem',
    },
  },
  h2: {
    fontSize: '2.5rem',
    lineHeight: 1.3,
    [theme.breakpoints.down('lg')]: {
      fontSize: '2.25rem',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.75rem',
    },
    '@media (max-width: 480px)': {
      fontSize: '1.5rem',
    },
  },
  h3: {
    fontSize: '2rem',
    lineHeight: 1.4,
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.875rem',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '1.75rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
    '@media (max-width: 480px)': {
      fontSize: '1.25rem',
    },
  },
  subtitle: {
    fontSize: '1.125rem',
    lineHeight: 1.6,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.0625rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
    '@media (max-width: 480px)': {
      fontSize: '0.9375rem',
    },
  },
  body: {
    fontSize: '1rem',
    lineHeight: 1.6,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9375rem',
    },
    '@media (max-width: 480px)': {
      fontSize: '0.875rem',
    },
  },
});

// Consistent spacing between elements
export const getSpacing = (theme: Theme) => ({
  sectionGap: {
    marginBottom: '80px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '60px',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: '40px',
    },
  },
  elementGap: {
    marginBottom: '40px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '32px',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: '24px',
    },
  },
  smallGap: {
    marginBottom: '20px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '16px',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: '12px',
    },
  },
});

// Consistent grid layouts
export const getGridStyles = (theme: Theme) => ({
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '40px',
    [theme.breakpoints.down('md')]: {
      gap: '32px',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: '24px',
    },
  },
  threeColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px',
    [theme.breakpoints.down('lg')]: {
      gap: '24px',
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
  fourColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
});

// Consistent button styles
export const getButtonStyles = (theme: Theme) => ({
  large: {
    padding: '16px 32px',
    fontSize: '1.125rem',
    [theme.breakpoints.down('md')]: {
      padding: '14px 28px',
      fontSize: '1.0625rem',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '12px 24px',
      fontSize: '1rem',
    },
    '@media (max-width: 480px)': {
      padding: '10px 20px',
      fontSize: '0.9375rem',
    },
  },
  medium: {
    padding: '12px 24px',
    fontSize: '1rem',
    [theme.breakpoints.down('md')]: {
      padding: '10px 20px',
      fontSize: '0.9375rem',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '8px 16px',
      fontSize: '0.875rem',
    },
  },
});

// Consistent card styles
export const getCardStyles = (theme: Theme) => ({
  borderRadius: '16px',
  padding: '32px',
  [theme.breakpoints.down('md')]: {
    padding: '24px',
    borderRadius: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    borderRadius: '10px',
  },
  '@media (max-width: 480px)': {
    padding: '16px',
    borderRadius: '8px',
  },
});

// Breakpoint helpers
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

export const isMobileBreakpoint = '@media (max-width: 899px)';
export const isTabletBreakpoint = '@media (min-width: 600px) and (max-width: 899px)';
export const isDesktopBreakpoint = '@media (min-width: 900px)';